import * as glassEasel from 'glass-easel';
import { SelectorQuery } from './selector_query';
import { IntersectionObserver } from './intersection';
import { ResizeObserver } from './resize';
import { MediaQueryObserver } from './media_query';
const filterComponentExportWithType = (source, elem, componentType) => {
    var _a;
    const comp = elem.asInstanceOf(componentType._$);
    if (comp === null)
        return undefined;
    const selectedSpace = comp.getRootBehavior().ownerSpace;
    const uncheckedCaller = comp.getMethodCaller();
    const caller = uncheckedCaller instanceof ComponentCaller ? uncheckedCaller : null;
    const sourceSpace = source._$.getRootBehavior().ownerSpace;
    const defaultResult = selectedSpace !== sourceSpace ? null : caller;
    const ret = (_a = caller === null || caller === void 0 ? void 0 : caller._$export) === null || _a === void 0 ? void 0 : _a.call(caller, selectedSpace !== sourceSpace ? null : source);
    if (ret === undefined) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return defaultResult;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return ret;
};
const filterComponentExport = (source, elem) => {
    var _a;
    if (glassEasel.Component.isComponent(elem)) {
        const selectedSpace = elem.getRootBehavior().ownerSpace;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const uncheckedCaller = elem.getMethodCaller();
        const caller = uncheckedCaller instanceof ComponentCaller ? uncheckedCaller : null;
        const sourceSpace = source._$.getRootBehavior().ownerSpace;
        const defaultResult = selectedSpace !== sourceSpace ? null : caller;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const ret = (_a = caller === null || caller === void 0 ? void 0 : caller._$export) === null || _a === void 0 ? void 0 : _a.call(caller, selectedSpace !== sourceSpace ? null : source);
        if (ret === undefined) {
            return defaultResult;
        }
        return ret;
    }
    return undefined;
};
export const determineComponentExports = (parent, selfExport) => {
    if (selfExport !== undefined)
        return selfExport;
    for (let i = parent.length - 1; i >= 0; i -= 1) {
        const behavior = parent[i];
        if (behavior._$export !== undefined)
            return behavior._$export;
    }
    return undefined;
};
export class ComponentCaller {
    /** The component path in the code space */
    get is() {
        return this._$.is;
    }
    set is(value) {
        Object.defineProperty(this, 'is', {
            value,
            writable: true,
            enumerable: true,
            configurable: true,
        });
    }
    /** The `id` field in the template */
    get id() {
        return this._$.id;
    }
    set id(value) {
        Object.defineProperty(this, 'id', {
            value,
            writable: true,
            enumerable: true,
            configurable: true,
        });
    }
    /** The `data-*` field in the template */
    get dataset() {
        return this._$.dataset;
    }
    set dataset(value) {
        Object.defineProperty(this, 'dataset', {
            value,
            writable: true,
            enumerable: true,
            configurable: true,
        });
    }
    /** The component data and property values */
    get data() {
        return this._$.data;
    }
    set data(value) {
        Object.defineProperty(this, 'data', {
            value,
            writable: true,
            enumerable: true,
            configurable: true,
        });
    }
    /** The component data and property values (same as `data` ) */
    get properties() {
        return this._$.data;
    }
    set properties(value) {
        Object.defineProperty(this, 'properties', {
            value,
            writable: true,
            enumerable: true,
            configurable: true,
        });
    }
    /**
     * Group several data update calls
     *
     * This method is designed for hinting the backend that some updates should be handled together.
     * However, this is done automatically now,
     * so this method is just for backward compatibilities.
     */
    // eslint-disable-next-line class-methods-use-this
    groupSetData(callback) {
        glassEasel.safeCallback('GroupSetData Callback', callback, this, [], this._$);
    }
    setData(data, callback) {
        this._$.setData(data);
        if (callback) {
            glassEasel.triggerRender(this._$, () => {
                glassEasel.safeCallback('SetData Callback', callback, this, [], this._$);
            });
        }
    }
    updateData(newData) {
        this._$.updateData(newData);
    }
    replaceDataOnPath(path, data) {
        this._$.replaceDataOnPath(path, data);
    }
    spliceArrayDataOnPath(path, index, del, inserts) {
        this._$.spliceArrayDataOnPath(path, index, del, inserts);
    }
    /**
     * Apply all scheduled updates immediately
     *
     * Inside observers, it is generally not .
     */
    applyDataUpdates() {
        this._$.applyDataUpdates();
    }
    /**
     * Pending all data updates in the callback, and apply updates after callback returns
     *
     * This function helps grouping several `replaceDataOnPath` or `spliceArrayDataOnPath` calls,
     * and then apply them at the end of the callback.
     * `setData` and `applyDataUpdates` calls inside the callback still apply updates immediately.
     */
    groupUpdates(callback) {
        return this._$.groupUpdates(() => glassEasel.safeCallback('GroupUpdates Callback', callback, this, [], this._$));
    }
    /**
     * Check whether the `other` behavior is a dependent behavior or a implemented trait behavior
     */
    hasBehavior(behavior) {
        return this._$.hasBehavior(behavior._$);
    }
    /**
     * Get the trait behavior implementation of the component
     *
     * Returns `undefined` if the specified trait behavior is not implemented.
     */
    traitBehavior(traitBehavior) {
        return this._$.traitBehavior(traitBehavior._$);
    }
    /** Trigger an event */
    triggerEvent(name, detail, options) {
        return this._$.triggerEvent(name, detail, options);
    }
    /** Create a selector query for searching element inside the component */
    createSelectorQuery() {
        return new SelectorQuery(this);
    }
    /** Create an intersection observer */
    createIntersectionObserver(options) {
        return new IntersectionObserver(this, (options === null || options === void 0 ? void 0 : options.thresholds) || [0], (options === null || options === void 0 ? void 0 : options.initialRatio) || 0, (options === null || options === void 0 ? void 0 : options.observeAll) || false);
    }
    /** Create a resize observer */
    createResizeObserver(options) {
        return new ResizeObserver(this, !!(options === null || options === void 0 ? void 0 : options.observeAll));
    }
    /** Create an media query observer */
    createMediaQueryObserver() {
        return new MediaQueryObserver(this);
    }
    selectComponent(selector, componentType) {
        var _a, _b;
        const target = this._$.getShadowRoot().querySelector(selector);
        if (target === null)
            return null;
        return componentType
            ? (_a = filterComponentExportWithType(this, target, componentType)) !== null && _a !== void 0 ? _a : null
            : (_b = filterComponentExport(this, target)) !== null && _b !== void 0 ? _b : null;
    }
    selectAllComponents(selector, componentType) {
        const targets = this._$.getShadowRoot().querySelectorAll(selector);
        const ret = [];
        targets.forEach((target) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const r = componentType
                ? filterComponentExportWithType(this, target, componentType)
                : filterComponentExport(this, target);
            if (r !== undefined)
                ret.push(r);
        });
        return ret;
    }
    selectOwnerComponent(componentType) {
        var _a, _b, _c;
        const target = (_a = this._$.ownerShadowRoot) === null || _a === void 0 ? void 0 : _a.getHostNode();
        if (target === undefined)
            return null;
        return componentType
            ? (_b = filterComponentExportWithType(this, target, componentType)) !== null && _b !== void 0 ? _b : null
            : (_c = filterComponentExport(this, target)) !== null && _c !== void 0 ? _c : null;
    }
    getRelationNodes(relationKey) {
        return this._$.getRelationNodes(relationKey).map((target) => target.getMethodCaller());
    }
    /** Cast the component into a general-typed one */
    general() {
        return this;
    }
    /**
     * Cast the component into the specified type
     *
     * Returns `null` if the component node is not the instance of the specified component.
     */
    asInstanceOf(componentType) {
        const inner = this._$.asInstanceOf(componentType._$);
        if (!inner)
            return null;
        return this;
    }
}
export class ComponentProto {
    constructor(methods, parents, componentExport) {
        this.proto = Object.create(ComponentCaller.prototype);
        Object.assign(this.proto, methods);
        this.proto._$export = determineComponentExports(parents, componentExport);
    }
    derive() {
        return Object.create(this.proto);
    }
}
//# sourceMappingURL=component.js.map